import React, { CSSProperties, useMemo, useRef, useState } from 'react';
import styles from './language-switcher.module.scss';
import useOnClickOutside from '../../../hooks/use-on-click-outside.hook';
import { ChevronDown, GlobeIcon, TickIcon } from '../icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { ApplicationType } from '../../../constants/application-type';

type Locale = { text: string; value: string };

export type Props = {
  locales: Locale[];
  activeLocale: string;
  themeCopyColor?: string;
  brandPrimaryColor?: string;
  application?: ApplicationType;
  invertedTheme?: boolean;
  customClass?: string;
};

const getLocaleUrl = (locales: Locale[], newLocaleCode: string): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const { origin, pathname, search } = window.location;
  const urlPaths = pathname.split('/').filter(Boolean);

  if (locales.some((locale) => locale.value === urlPaths[0])) {
    urlPaths[0] = newLocaleCode;
  } else {
    urlPaths.unshift(newLocaleCode);
  }

  return `${origin}/${urlPaths.join('/')}${search}`;
};

export default function LanguageSwitcher({
  locales,
  activeLocale,
  themeCopyColor,
  application,
  brandPrimaryColor,
  invertedTheme,
  customClass,
}: Props) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const [currentLocale, setCurrentLocale] = useState<Locale | null>(
    locales.find((locale) => locale.value === activeLocale) || null,
  );

  const toggleDropdown = () => setIsOpen(!isOpen);

  const activeColor = useMemo(() => {
    if (application === ApplicationType.PartnerPortal) {
      return colorStyle.blue100;
    } else if (application === ApplicationType.IntoStudy) {
      return colorStyle.turquoise100;
    } else {
      return brandPrimaryColor;
    }
  }, [application, brandPrimaryColor]);

  const renderData = useMemo(() => {
    const menuOptions = locales.map(({ value, text }) => ({
      url: getLocaleUrl(locales, value),
      value,
      text,
    }));

    const buttonStyle = {
      '--language-switcher-color': themeCopyColor,
      '--language-switcher-active-color': activeColor,
    } as CSSProperties;

    return { menuOptions, buttonStyle };
  }, [activeColor, locales, themeCopyColor]);

  useOnClickOutside(ref, () => setIsOpen(false));

  return (
    <button
      className={`${styles['language-button']} position-relative ${isOpen ? styles['language-button__open'] : ''} ${invertedTheme ? styles['inverted-theme'] : ''} ${styles[customClass]}`}
      onClick={toggleDropdown}
      ref={ref}
      type='button'
      title='Language'
      style={renderData.buttonStyle}
      aria-expanded={isOpen}
      aria-label={`${isOpen ? 'Close' : 'Open'} language switcher`}
    >
      <div className={`${styles['language-icon']} image-wrapper`}>
        <GlobeIcon width={24} height={24} fillColor={colorStyle.white} />
      </div>
      <span className='d-inline fw-bold'>{currentLocale?.text}</span>
      <div className={`${styles['language-chevron']} image-wrapper d-inline`}>
        <ChevronDown fillColor={colorStyle.white} height={18} width={16} />
      </div>
      {isOpen && (
        <div className={styles['language-menu']}>
          {renderData.menuOptions.map(({ url, value, text }) => (
            <a
              key={value}
              href={url}
              onClick={() => setCurrentLocale({ value, text })}
              className={`${styles['menu-item']} ${styles[value === currentLocale?.value ? 'active' : '']}`}
            >
              <span>{text}</span>
              {value === currentLocale?.value && (
                <TickIcon fillColor={activeColor} height={18} width={16} />
              )}
            </a>
          ))}
        </div>
      )}
    </button>
  );
}
